import { useState, useEffect } from 'react';
import { AssetsListWithNames } from './AssetsListWithNames';
import { supabase } from '../../lib/supabase';

interface AuctionSummaryCardProps {
  auction: {
    auction_id: number;
    league_name: string;
    initiator_username: string;
    target_username: string;
    original_sender_assets: {
      players?: number[];
      picks?: number[];
      goalies?: number[];
    };
    original_receiver_assets: {
      players?: number[];
      picks?: number[];
      goalies?: number[];
    };
    expires_at: string;
    bid_count: number;
    latest_bid_username?: string;
    winning_bid_id?: number | null;
    grouped_count?: number;
    time_remaining_seconds?: number;
  };
  onClick: () => void;
  onCounterOffer?: (auctionId: number) => void;
}

interface WinningBid {
  id: number;
  auction_id: number;
  bidder_user_id: number;
  bidder_username?: string | null;
  bid_sender_assets?: {
    players?: number[];
    picks?: number[];
    goalies?: number[];
  } | null;
  bid_receiver_assets?: {
    players?: number[];
    picks?: number[];
    goalies?: number[];
  } | null;
  created_at?: string | null;
  is_winning?: boolean | null;
  target_side?: string | null;
}

export function AuctionSummaryCard({ auction, onClick, onCounterOffer }: AuctionSummaryCardProps) {
  console.log('🔍 AuctionSummaryCard rendered:', auction.auction_id, 'onCounterOffer:', !!onCounterOffer);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [winningBid, setWinningBid] = useState<WinningBid | null>(null);

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const expires = new Date(auction.expires_at);
      const diff = expires.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeRemaining('Expiré');
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [auction.expires_at]);

  // Fetch the winning bid details when winning_bid_id is present
  useEffect(() => {
    let mounted = true;
    const fetchWinningBid = async () => {
      if (!auction.winning_bid_id) {
        setWinningBid(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('trade_bids')
          .select('*')
          .eq('id', auction.winning_bid_id)
          .single();

        if (error) {
          console.error('Error fetching winning bid', error);
          if (mounted) setWinningBid(null);
          return;
        }

        if (mounted) setWinningBid(data || null);
      } catch (err) {
        console.error('Unexpected error fetching winning bid', err);
        if (mounted) setWinningBid(null);
      }
    };

    fetchWinningBid();
    return () => { mounted = false; };
  }, [auction.winning_bid_id]);

  const getUrgencyClass = () => {
    const now = new Date();
    const expires = new Date(auction.expires_at);
    const diff = expires.getTime() - now.getTime();
    const hoursRemaining = diff / (1000 * 60 * 60);
    
    if (hoursRemaining < 0) return 'text-error';
    if (hoursRemaining < 1) return 'text-error';
    if (hoursRemaining < 3) return 'text-warning';
    return 'text-primary';
  };

  const getBorderClass = () => {
    if (auction.winning_bid_id) return 'border-success/40 bg-success/5';
    
    const now = new Date();
    const expires = new Date(auction.expires_at);
    const diff = expires.getTime() - now.getTime();
    const hoursRemaining = diff / (1000 * 60 * 60);
    
    if (hoursRemaining < 1) return 'border-error/40 bg-error/5';
    if (hoursRemaining < 3) return 'border-warning/40 bg-warning/5';
    return 'border-primary/20';
  };

  return (
    <div
      className={`card bg-base-100 border-2 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200 ${getBorderClass()}`}
      onClick={() => {
        console.log('🔔 AuctionSummaryCard click:', { auction_id: auction.auction_id, auction_prop_id: auction.auction_id ?? auction });
        try { onClick(); } catch (err) { console.error('Error calling onClick from AuctionSummaryCard', err); }
      }}
    >
      <div className="card-body p-4">
        {/* Header avec badges */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="badge badge-primary">{auction.league_name}</span>
              <span className="badge badge-outline">
                {auction.bid_count} offre{auction.bid_count > 1 ? 's' : ''}
              </span>
              {auction.winning_bid_id && (
                <span className="badge badge-success">✅ Acceptée</span>
              )}
            </div>
            
            <h4 className="font-semibold text-base mb-2 flex items-center gap-2">
              <span>{auction.initiator_username} → {auction.target_username}</span>
              {auction.grouped_count && auction.grouped_count > 1 && (
                <span className="badge badge-info">{auction.grouped_count} offres</span>
              )}
            </h4>
            
            {auction.latest_bid_username && (
              <p className="text-sm opacity-70">
                Dernière offre de <strong>{auction.latest_bid_username}</strong>
              </p>
            )}
          </div>
          
          <div className="text-right">
            <div className={`text-sm font-semibold mb-1 ${getUrgencyClass()}`}>
              ⏰ {timeRemaining}
            </div>
          </div>
        </div>
        
        {/* Aperçu de la transaction originale */}
        <div className="bg-base-200 p-3 rounded text-sm mb-3">
          <h5 className="font-medium mb-2 opacity-70">Transaction originale :</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="opacity-70 mb-1 text-xs">Offre :</p>
              <div className="text-xs opacity-70 mb-2">Par : <strong>{auction.initiator_username}</strong></div>
              <AssetsListWithNames assets={auction.original_sender_assets} compact />
            </div>
            <div>
              <p className="opacity-70 mb-1 text-xs">Contre :</p>
              <div className="text-xs opacity-70 mb-2">Par : <strong>{auction.target_username}</strong></div>
              <AssetsListWithNames assets={auction.original_receiver_assets} compact />
            </div>
          </div>

        {/* Afficher l'offre gagnante actuelle si disponible */}
        {winningBid && (
          <div className="bg-success/5 border border-success/30 p-3 rounded text-sm mb-3">
            <h5 className="font-medium mb-2 opacity-70">Offre gagnante actuelle :</h5>
            <div className="text-xs opacity-70 mb-2">Par : <strong>{winningBid.bidder_username || winningBid.bidder_user_id}</strong></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="opacity-70 mb-1 text-xs">Offre (envoyeur) :</p>
                <AssetsListWithNames assets={winningBid.bid_sender_assets ?? {}} compact />
              </div>
              <div>
                <p className="opacity-70 mb-1 text-xs">Contre (receveur) :</p>
                <AssetsListWithNames assets={winningBid.bid_receiver_assets ?? {}} compact />
              </div>
            </div>
          </div>
        )}
        </div>
        
        {/* Statut et action */}
        <div className="flex justify-between items-center pt-2 border-t border-base-300">
          <div className="text-xs opacity-60">
            Créée {new Date(auction.expires_at).getTime() - 24 * 60 * 60 * 1000 > 0 ? 
              'il y a ' + Math.floor((Date.now() - (new Date(auction.expires_at).getTime() - 24 * 60 * 60 * 1000)) / (1000 * 60 * 60)) + 'h'
              : 'récemment'
            }
          </div>
            <div className="flex gap-2">
            <button 
              className="btn btn-outline btn-sm"
              onClick={(e) => { e.stopPropagation(); console.log('🔍 Détails button click for', auction.auction_id); try { onClick(); } catch(err) { console.error(err); } }}
            >
              👁️ Détails
            </button>
            {onCounterOffer && (
              <button 
                className="btn btn-primary btn-sm"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  onCounterOffer(auction.auction_id); 
                }}
              >
                🔥 Contre-offre
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}